// POS → Restaurant → Meseros (/pos/rest-meseros). Meseros con PIN (bcrypt,
// nunca se muestra) y bandera de SUPERVISOR (autoriza anulaciones de líneas
// comandadas). Gestión solo administradores (la RPC lo exige).
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Switch, fieldCls, posRpc } from './shared';
import type { RepartidorT } from './restaurante';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };

interface MeseroT {
  id: string; nombre: string; partner_id: string | null;
  es_supervisor: boolean; activo: boolean; pin_configurado: boolean;
}
interface PartnerOptT { id: string; razon_social: string; tipo_partner: string[] }

const FORM0 = { nombre: '', pin: '', partner_id: '', es_supervisor: false, activo: true };

export function MeserosRestaurant() {
  const qc = useQueryClient();
  const [form, setForm] = useState<typeof FORM0 | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  const { data: meseros = [] } = useQuery<MeseroT[]>({
    queryKey: ['rest-meseros'],
    queryFn: () => posRpc<MeseroT[]>('rest_meseros_listar', {}),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: partners = [] } = useQuery<PartnerOptT[]>({
    queryKey: ['fin-partners'],
    queryFn: async () => (await apiClient.post('/rpc/sri_partners', {}, FIN)).data || [],
  });
  const empleados = useMemo(
    () => partners.filter((p) => (p.tipo_partner ?? []).includes('empleado')), [partners]);

  const guardar = async () => {
    if (!form) return;
    try {
      await posRpc('rest_mesero_guardar', {
        _data: { nombre: form.nombre, pin: form.pin || null, partner_id: form.partner_id || null,
          es_supervisor: form.es_supervisor, activo: form.activo },
        _id: editId,
      });
      void qc.invalidateQueries({ queryKey: ['rest-meseros'] });
      toast.success(editId ? 'Mesero actualizado' : 'Mesero creado');
      setForm(null); setEditId(null);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Users className="w-5 h-5" /> Meseros</h1>
        <Button size="sm" onClick={() => { setEditId(null); setForm({ ...FORM0 }); }}>
          <Plus className="w-4 h-4 mr-1" /> Nuevo mesero
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Personal de salón (login por PIN, sin sesión de caja)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b">
                <th className="py-2 pr-3 text-right w-10">N°</th>
                <th className="py-2 pr-3 text-left">Nombre</th>
                <th className="py-2 pr-3 text-center">Supervisor</th>
                <th className="py-2 pr-3 text-center">PIN</th>
                <th className="py-2 pr-3 text-center">Activo</th>
                <th className="py-2 pr-3" />
              </tr>
            </thead>
            <tbody>
              {meseros.map((m, i) => (
                <tr key={m.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{m.nombre}</td>
                  <td className="py-2 pr-3 text-center">{m.es_supervisor ? '★' : '—'}</td>
                  <td className="py-2 pr-3 text-center">{m.pin_configurado ? '••••' : <span className="text-red-500">sin PIN</span>}</td>
                  <td className="py-2 pr-3 text-center">{m.activo ? '✓' : '—'}</td>
                  <td className="py-2 pr-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => {
                      setEditId(m.id);
                      setForm({ nombre: m.nombre, pin: '', partner_id: m.partner_id ?? '', es_supervisor: m.es_supervisor, activo: m.activo });
                    }}><Pencil className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {!meseros.length && (
                <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">
                  Sin meseros. Crea al menos uno con PIN (y marca un SUPERVISOR para poder anular comandas).
                </td></tr>
              )}
            </tbody>
          </table>

          {form && (
            <div className="border rounded-md p-3 space-y-3">
              <div className="text-sm font-medium">{editId ? 'Editar mesero' : 'Nuevo mesero'}</div>
              <div className="grid gap-3 md:grid-cols-3">
                <Campo label="Nombre *">
                  <input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
                </Campo>
                <Campo label={editId ? 'PIN (vacío = conservar)' : 'PIN (4-6 dígitos) *'}>
                  <input className={`${fieldCls} font-mono`} type="password" inputMode="numeric" maxLength={6}
                         autoComplete="new-password" value={form.pin}
                         onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '') })} />
                </Campo>
                <Campo label="Empleado (opcional, enlaza a nómina)">
                  <select className={fieldCls} value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })}>
                    <option value="">— Ninguno —</option>
                    {empleados.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                  </select>
                </Campo>
              </div>
              <div className="flex items-center gap-6">
                <Switch checked={form.es_supervisor} onChange={(v) => setForm({ ...form, es_supervisor: v })}
                        label="Supervisor (autoriza anulaciones de líneas comandadas)" />
                <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label="Activo" />
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setForm(null); setEditId(null); }}>Cancelar</Button>
                  <Button size="sm" onClick={() => void guardar()}
                          disabled={!form.nombre.trim() || (!editId && form.pin.length < 4)}>Guardar</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Repartidores />
    </div>
  );
}

function Repartidores() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const { data: repartidores = [] } = useQuery<RepartidorT[]>({
    queryKey: ['rest-repartidores'],
    queryFn: () => posRpc<RepartidorT[]>('rest_repartidores_listar', {}),
    staleTime: 0, refetchOnMount: 'always',
  });
  const rpc = async (body: Record<string, unknown>, ok: string) => {
    try {
      await posRpc('rest_repartidor_guardar', body);
      void qc.invalidateQueries({ queryKey: ['rest-repartidores'] });
      toast.success(ok);
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <Card>
      {dlg.ui}
      <CardHeader className="pb-2"><CardTitle className="text-base">Repartidores (delivery)</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground border-b">
              <th className="py-2 pr-3 text-right w-10">N°</th>
              <th className="py-2 pr-3 text-left">Nombre</th>
              <th className="py-2 pr-3 text-left">Teléfono</th>
              <th className="py-2 pr-3 text-center">Activo</th>
              <th className="py-2 pr-3" />
            </tr>
          </thead>
          <tbody>
            {repartidores.map((r, i) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 pr-3 font-medium">{r.nombre}</td>
                <td className="py-2 pr-3">{r.telefono ?? '—'}</td>
                <td className="py-2 pr-3 text-center">
                  <Switch checked={r.activo} label=""
                          onChange={(v) => void rpc({ _data: { activo: v }, _id: r.id }, 'Repartidor actualizado')} />
                </td>
                <td className="py-2 pr-3 text-right">
                  <Button size="sm" variant="ghost" onClick={async () => {
                    if (await dlg.confirmar(`¿Eliminar a "${r.nombre}"?`, { peligro: true }))
                      void rpc({ _data: {}, _id: r.id, _eliminar: true }, 'Repartidor eliminado');
                  }}>✕</Button>
                </td>
              </tr>
            ))}
            {!repartidores.length && (
              <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">Sin repartidores.</td></tr>
            )}
          </tbody>
        </table>
        <div className="flex gap-2 items-end">
          <Campo label="Nombre">
            <input className={`${fieldCls} !w-56`} value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </Campo>
          <Campo label="Teléfono">
            <input className={`${fieldCls} !w-40`} value={telefono} onChange={(e) => setTelefono(e.target.value)} />
          </Campo>
          <Button size="sm" disabled={!nombre.trim()} onClick={() => {
            void rpc({ _data: { nombre: nombre.trim(), telefono } }, 'Repartidor creado');
            setNombre(''); setTelefono('');
          }}><Plus className="w-4 h-4 mr-1" /> Agregar</Button>
        </div>
      </CardContent>
    </Card>
  );
}
